import api from "../api";
import ApiConstant from "../../constants/api.constants";
import {
  UserRewardDetailsResponse,
  ProductRewardRate,
  CreateProductRewardRateDTO,
  UpdateProductRewardRateDTO,
  ProductRewardRateSearchDTO,
  ProductRewardRateSearchResponse,
  ProductRewardRateApiResponse,
  AdminDashboardRequest,
  AdminDashboardMetrics,
  UserRewardSummaryRequest,
  UserRewardSummaryResponse,
  UserReferral,
  UserRewardTransactionRequest,
  AdminRewardTransactionResponse,
} from "../../models/rewards-model";

 const getUserRewardDetails = async (userId: number) => {
  const response = await api.post(
    `${ApiConstant.getUserRewardDetails}?userId=${userId}`
  );
  return response.data; 
};

// Product Reward Rate APIs
const searchProductRewardRates = async (
  searchParams: ProductRewardRateSearchDTO
): Promise<ProductRewardRateSearchResponse> => {
  try {
    const response = await api.post(ApiConstant.searchProductRewardRate, searchParams);
    return response.data;
  } catch (error) {
    console.error("Error searching product reward rates:", error);
    return {
      data: [],
      pageNumber: 1,
      pageSize: 10,
      totalRecords: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

const getProductRewardRateById = async (id: number): Promise<ProductRewardRate | null> => {
  try {
    const response = await api.get(`${ApiConstant.getProductRewardRateById}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error getting product reward rate:", error);
    return null;
  }
};

const createProductRewardRate = async (
  data: CreateProductRewardRateDTO
): Promise<ProductRewardRateApiResponse> => {
  try {
    const response = await api.post(ApiConstant.createProductRewardRate, data);
    return response.data;
  } catch (error) {
    console.error("Error creating product reward rate:", error);
    throw error;
  }
};

const updateProductRewardRate = async (
  data: UpdateProductRewardRateDTO
): Promise<ProductRewardRateApiResponse> => {
  try {
    const response = await api.put(ApiConstant.updateProductRewardRate, data);
    return response.data;
  } catch (error) {
    console.error("Error updating product reward rate:", error);
    throw error;
  }
};

const deleteProductRewardRate = async (
  id: number
): Promise<ProductRewardRateApiResponse> => {
  try {
    const response = await api.delete(`${ApiConstant.deleteProductRewardRate}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting product reward rate:", error);
    throw error;
  }
};

export interface ProductForReward {
  productID: number;  
  name: string;
  isDeleted: boolean;
}

const getProductsForReward = async (): Promise<ProductForReward[]> => {
  try {
    const response = await api.get(ApiConstant.getProductsForReward);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching products for reward:", error);
    return [];
  }
};

// Admin Dashboard APIs
const getAdminDashboardMetrics = async (
  request: AdminDashboardRequest
): Promise<AdminDashboardMetrics> => {
  try {
    const response = await api.post(ApiConstant.adminRewardDashboard, request);
    return response.data;
  } catch (error) {
    console.error("Error fetching admin dashboard metrics:", error);
    return {
      totalEarned: 0,
      totalAvailablePoints: 0,
      totalPointsRedeemed: 0,
      totalExpiredPoints: 0,
      rewardToCurrencyRatio: 0,
    };
  }
};

const getUserRewardSummary = async (
  request: UserRewardSummaryRequest
): Promise<UserRewardSummaryResponse> => {
  try {
    const response = await api.post(ApiConstant.adminUserRewardSummary, request);
    return response.data;
  } catch (error) {
    console.error("Error fetching user reward summary:", error);
    return {
      data: [],
      pageNumber: 1,
      pageSize: 10,
      totalRecords: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

const getUserReferrals = async (userId: number): Promise<UserReferral[]> => {
  try {
    const response = await api.get(`${ApiConstant.adminUserReferrals}/${userId}`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching user referrals:", error);
    return [];
  }
};

const getUserRewardTransactions = async (
  request: UserRewardTransactionRequest
): Promise<AdminRewardTransactionResponse> => {
  try {
    const response = await api.post(ApiConstant.adminUserRewardTransactions, request);
    return response.data;
  } catch (error) {
    console.error("Error fetching user reward transactions:", error);
    return {
      data: [],
      pageNumber: 1,
      pageSize: 50,
      totalRecords: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

const RewardsService = {
  getUserRewardDetails,
  searchProductRewardRates,
  getProductRewardRateById,
  createProductRewardRate,
  updateProductRewardRate,
  deleteProductRewardRate,
  getProductsForReward,
  getAdminDashboardMetrics,
  getUserRewardSummary,
  getUserReferrals,
  getUserRewardTransactions,
};

export default RewardsService;
